import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TooltipOptions, YAxisPlotLinesOptions } from 'highcharts';

import { QuoteTimeSeriesType } from '../domain';
import { DetailedQuote } from '../../../../domain/public-api';
import { FormatConfig, PlotData } from '../domain/detailed-chart.domain';
import { DateTimeFormat, DateTimeFormatType } from '../../../../services/date/date-formatter.service';
import { ChartsConfigService } from '../../../../components/charts/service/charts-config.service';
import { NumberService } from '../../../../services/number/number.service';
import { DetailedChartFormatterService } from './detailed-chart-formatter.service';
import { TickPositionsService } from './tick-positions.service';
import type Highcharts from 'highcharts/highstock';

/**
 * Detailed Chart Config Service
 *
 * Service configure chart option
 * highcharts: Not all types are used from highcharts because
 *             type definition is missing property
 */
@Injectable({
  providedIn: 'root'
})
export class DetailedChartConfigService {

  private static readonly DAILY_XAXIS_TICK_INTERVAL: number = 48;
  private static readonly MONTHLY_XAXIS_TICK_INTERVAL: number = 80;
  private static readonly THREE_MONTH_XAXIS_TICK_INTERVAL: number = 100;
  private static readonly XAXIS_TICK_LABEL_FORMAT_INTRADAY: DateTimeFormat = DateTimeFormat.HOUR_MINUTE;
  private static readonly XAXIS_TICK_LABEL_FORMAT_YEARLY: DateTimeFormat = DateTimeFormat.YEAR;

  // Default color options for all types of charts
  // COMMON_COLOR_CONFIGURATION is type of Highcharts.PlotOptions
  // Type is set as any as type definition of PlotOptions is missing 'candlestick' property
  private static readonly COMMON_COLOR_CONFIGURATION: unknown = {
    line: {
      color: '#008a00'
    },
    candlestick: {
      color: '#ae1100',
      upColor: '#008a00',
      lineColor: '#ae1100',
      upLineColor: '#008a00'
    },
    area: {
      color: '#008a00',
      fillColor: {
        linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
        stops: [
          [0.6, 'rgba(242,249,242,1)'],
          [1, 'rgba(255,255,255,0)']
        ]
      }
    },
    column: {
      color: '#008a00',
      borderRadius: 0
    }
  };

  private static readonly TOOLTIP_OPTIONS: TooltipOptions = {
    positioner(labelWidth: number,
               _labelHeight: number,
               point: Highcharts.Point | Highcharts.TooltipPositionerPointObject): { x: number; y: number } {
      const plottablePoint = point as Highcharts.TooltipPositionerPointObject;
      if (plottablePoint.plotX > ((this.chart.chartWidth - 5) - labelWidth)) {
        return { x: ((this.chart.chartWidth - 5) - labelWidth) - 5, y: plottablePoint.plotY + 20 };
      } else {
        return { x: plottablePoint.plotX - 1, y: plottablePoint.plotY + 20 };
      }
    },
    outside: false,
    backgroundColor: 'rgba(247, 252, 247, 0.75)',
    borderColor: '#96cb95',
    style: {
      color: '#1c1c1c',
      fontSize: '14px',
      padding: '8px'
    }
  };

  protected readonly XAXIS_TICK_LABEL_FORMAT_MONTHLY: DateTimeFormatType;

  constructor(
    private translateService: TranslateService,
    private chartConfigService: ChartsConfigService,
    protected tickPositionsService: TickPositionsService,
    private chartFormatterService: DetailedChartFormatterService
  ) {
    this.XAXIS_TICK_LABEL_FORMAT_MONTHLY = this.translateService
      .instant('tdWb.markets.quote.chartConfig.TICK_LABEL_FORMAT_MONTHLY');
  }

  /**
   * Add volume chart series to chart config
   *
   * @param chartConfig
   */
  addChartVolume(chartConfig: Highcharts.Options): void {
    this.chartConfigService
      .createUpdateConfigBuilder(chartConfig)
      .addSeries(this.createVolume([]));
  }

  /**
   * Update volume chart series of chart config
   *
   * @param chartConfig
   * @param volumeData
   */
  updateChartVolume(chartConfig: Highcharts.Options, volumeData: Array<unknown>): void {
    this.chartConfigService
      .createUpdateConfigBuilder(chartConfig)
      .updateSeries(1, volumeData);
  }

  /**
   * Get XAxis tick label format for monthly ticks
   *
   * @returns no of decimal places
   */
  getXAxisTickLabelFormatMontly(): string {
    return this.XAXIS_TICK_LABEL_FORMAT_MONTHLY;
  }

  /**
   * Get XAxis tick label format for intraday ticks
   *
   * @returns no of decimal places
   */
  getXAxisTickLabelFormatIntraDay(): string {
    return DetailedChartConfigService.XAXIS_TICK_LABEL_FORMAT_INTRADAY;
  }

  /**
   * Refresh close plot line (yAxis)
   *
   * @param quote
   * @param chartConfig
   */
  refreshClosePlotline(quote: DetailedQuote, chartConfig: Highcharts.Options): void {
    const linePlotData: PlotData = this.getLinePlotData(quote);
    chartConfig.yAxis[0].plotLines = [
      this.createPricePlotline(linePlotData)
    ];
  }

  /**
   * Given a quote, return an object which contains a price and a display name
   * for a line to be plotted on a chart.
   * The type of price return is dependent on the quote's IssueType. I.e., an option will
   * return a strike price as the price value.
   *
   * @param quote
   * @returns
   */
  getLinePlotData(quote: DetailedQuote): PlotData {
    return this.chartFormatterService.getLinePlotData(quote);
  }

  /**
   * Reset YAxis plot lines
   *
   * @param chartConfig
   */
  resetYAxisPlotlines(chartConfig: Highcharts.Options): void {
    const yAxis = chartConfig.yAxis;
    if (yAxis instanceof Array) {
      for (const y of yAxis) {
        y.plotLines = [];
        y.min = null;
        y.max = null;
      }
    }
  }

  /**
   * Update daily chart series
   *
   * @param seriesIndex
   * @param series
   * @param chartConfig
   * @param marketOpenTime milliseconds
   * @param isPennyStock
   */
  updateDailyChartSeries(seriesIndex: number, series: QuoteTimeSeriesType, chartConfig: Highcharts.Options,
                         marketOpenTime: number, isPennyStock: boolean = false): void {
    const formatConfig: FormatConfig = {
      xAxisTickInterval: DetailedChartConfigService.DAILY_XAXIS_TICK_INTERVAL,
      xAxisTickLabelFormatKey: DetailedChartConfigService.XAXIS_TICK_LABEL_FORMAT_INTRADAY,
      useStaticIntradayChart: true,
      marketOpenTime,
      marker: undefined,
      xAxisTickLabelFormatIncludedYear: undefined,
      xAxisOrdinal: undefined
    };
    this.updateChartSeries(seriesIndex, series, chartConfig, formatConfig, isPennyStock, false);
  }

  /**
   * Update monthly chart series
   */
  updateMonthlyChartSeries(seriesIndex: number, series: QuoteTimeSeriesType,
                           chartConfig: Highcharts.Options, isPennyStock: boolean = false): void {

    const formatConfig: FormatConfig = {
      xAxisTickInterval: DetailedChartConfigService.MONTHLY_XAXIS_TICK_INTERVAL,
      xAxisTickLabelFormatKey: this.XAXIS_TICK_LABEL_FORMAT_MONTHLY,
      useStaticIntradayChart: false,
      marketOpenTime: undefined,
      marker: undefined,
      xAxisTickLabelFormatIncludedYear: undefined,
      xAxisOrdinal: undefined
    };
    this.updateChartSeries(seriesIndex, series, chartConfig, formatConfig, isPennyStock);
  }

  /**
   * Update three-month chart series
   */
  updateThreeMonthChartSeries(seriesIndex: number, series: QuoteTimeSeriesType,
                              chartConfig: Highcharts.Options, isPennyStock: boolean = false): void {

    const formatConfig: FormatConfig = {
      xAxisTickInterval: DetailedChartConfigService.THREE_MONTH_XAXIS_TICK_INTERVAL,
      xAxisTickLabelFormatKey: this.XAXIS_TICK_LABEL_FORMAT_MONTHLY,
      useStaticIntradayChart: false,
      marketOpenTime: undefined,
      marker: undefined,
      xAxisTickLabelFormatIncludedYear: undefined,
      xAxisOrdinal: undefined
    };
    this.updateChartSeries(seriesIndex, series, chartConfig, formatConfig, isPennyStock);
  }

  /**
   * Update yearly chart series
   */
  updateYearlyChartSeries(seriesIndex: number, series: QuoteTimeSeriesType,
                          chartConfig: Highcharts.Options, isPennyStock: boolean = false): void {

    // Eagerly compute 52-week tick positions to satisfy dependent logic/tests and cache in Highcharts via tickPositioner.
    // Keep tickPositioner so Highcharts can recompute positions when needed.
    this.tickPositionsService.calculate52WeeksTicks(series);
    const xAxisTickPositioner = (): Array<number> => this.tickPositionsService.calculate52WeeksTicks(series);
    const formatConfig: FormatConfig = {
      xAxisTickPositioner,
      xAxisTickLabelFormatKey: DetailedChartConfigService.XAXIS_TICK_LABEL_FORMAT_YEARLY,
      useStaticIntradayChart: false,
      xAxisOrdinal: true,
      xAxisTickInterval: undefined,
      marketOpenTime: undefined,
      marker: undefined,
      xAxisTickLabelFormatIncludedYear: true
    };
    this.updateChartSeries(seriesIndex, series, chartConfig, formatConfig, isPennyStock);
  }

  /**
   * Highstocks won't display a plotline (i.e., a constant value) if it falls too far outside the
   * closeSeries range of y-values. This method can be used to calculate a new y-axis range
   * that will include all data points (the plotline and the closeSeries) on the chart. For example:
   *
   *  var range = detailedChartService.calculateRangeExtremities(plotLineValue, closeSeries);
   *  chart.yAxis[0].setExtremes(range[0], range[1]);
   *
   * @param plotLineValue The value for the chart's plotline.
   * @param seriesData A 2-dim array containing the [x,...,y] values to be rendered on the chart.
   * @returns An 1-dim array containing the y-axis min and max values for the y-axis. A null
   * value tells highchart to take responsibility for calculating the min/max value
   * for the axis (e.g. for the range [null, 5] highstock will calculate the min y-axis value).
   */
  calculateRangeExtremities(plotLineValue: number, seriesData: QuoteTimeSeriesType): Array<number> {
    const range: Array<number> = [null, null];
    if (seriesData.length <= 1 || seriesData[0].length === 0) {
      return [null, null];
    }
    const lengthArray = seriesData[0].length;
    const dataPoints = seriesData.map((x: unknown): number => x[lengthArray - 1]);
    dataPoints.push(plotLineValue);
    const min = Math.min(...dataPoints);
    const max = Math.max(...dataPoints);
    const buffer = (max - min) * 0.1;
    if (plotLineValue === min) {
      range[0] = (min - buffer);
    } else if (plotLineValue === max) {
      range[1] = (max + buffer);
    }
    return range;
  }

  /**
   * Create a stock chart and return chart config builder
   * Used on Top Movers
   *
   * @param quote quote data
   * @param useIntradayChart is it an intra day chart
   * @returns chart object
   */
  createStockChartConfig(quote: DetailedQuote, useIntradayChart: boolean = false): Highcharts.Options {
    const tickerSymbol: string = quote.tickerSymbolForTimeSeries;
    const seriesData: QuoteTimeSeriesType = quote.defaultTimeSeries.closeSeries;
    const linePlotData: PlotData = this.getLinePlotData(quote);
    const series: unknown = this.createSeries(tickerSymbol);
    const yAxisLabelvalue: number = DetailedChartFormatterService.decimalPlaces(quote.previousClose === undefined
      ? 10 : quote.previousClose, quote.isPennyStock);
    const yAxisLabelsFormat = `{value:,.${ yAxisLabelvalue }f}`;
    // yAxis -> Highcharts.AxisOptions type definition for autoRotation is number[];
    // Highchart doc: Set it to false to disable rotation, which will cause the labels to word-wrap if possible.
    const yAxis: unknown = [{
      labels: {
        autoRotation: false,
        format: yAxisLabelsFormat
      },
      offset: 50,
      opposite: true,
      plotLines: [this.createPricePlotline(linePlotData)]
    }];
    const config: Highcharts.Options = this.chartConfigService
      .createStockChartConfig()
      .addSeries(series)
      .setProperty({
        propertyName: 'plotOptions',
        propertyValue: DetailedChartConfigService.COMMON_COLOR_CONFIGURATION,
        defaultValue: {}
      })
      .setProperty({
        propertyName: 'scrollbar.enabled',
        propertyValue: false,
        defaultValue: true
      })
      .setProperty({
        propertyName: 'navigator.enabled',
        propertyValue: false,
        defaultValue: true
      })
      .setProperty({
        propertyName: 'rangeSelector.enabled',
        propertyValue: false,
        defaultValue: true
      })
      .setProperty({
        propertyName: 'xAxis.minPadding',
        propertyValue: 0.05,
        defaultValue: 0
      })
      .setProperty({
        propertyName: 'xAxis.labels.autoRotation',
        propertyValue: false,
        defaultValue: {}
      })
      .setProperty({
        propertyName: 'xAxis.units',
        propertyValue: [['day', [1]], ['week', [1]]],
        defaultValue: null
      })
      .setProperty({
        propertyName: 'legend',
        propertyValue: false,
        defaultValue: {}
      })
      .setYAxis(yAxis)
      .setProperty({
        propertyName: 'tooltip',
        propertyValue: DetailedChartConfigService.TOOLTIP_OPTIONS,
        defaultValue: {}
      })
      .getConfig();

    if (useIntradayChart === true) {
      this.updateDailyChartSeries(0, seriesData, config, quote.timeSeriesMarketTime.open, quote.isPennyStock);
    } else {
      this.updateMonthlyChartSeries(0, seriesData, config, quote.isPennyStock);
    }

    return config;
  }

  /**
   * Create a time series stock chart and return chart config builder
   * Used on holdings and Watchlists
   */
  createTimeSeriesStockChartConfig(quote: DetailedQuote): Highcharts.Options {
    const tickerSymbol = quote.tickerSymbolForTimeSeries;
    const series = this.createSeries(tickerSymbol);

    let previousClose: number = NumberService.isNumber(quote.previousClose) ? quote.previousClose : 10;
    previousClose = DetailedChartFormatterService.decimalPlaces(previousClose, quote.isPennyStock);

    const yAxisChartLabelsFormat = `{value:,.${ previousClose }f}`;
    // yAxis type of Array<Highcharts.AxisOptions>
    // but type definition is missing for height
    // TODO: check if height is used by highchart
    const yAxis: unknown = [{
      labels: {
        autoRotation: false,
        format: yAxisChartLabelsFormat,
        style: {
          color: '#1c1c1c'
        }
      },
      offset: 50,
      opposite: true,
      height: '70%'
    }, {
      labels: {
        autoRotation: false,
        formatter: this.chartFormatterService.getTimeSeriesFormatter(),
        style: {
          color: '#1c1c1c'
        }
      },
      top: '70%',
      height: '30%',
      offset: 50,
      opposite: true
    }];

    return this.chartConfigService
      .createStockChartConfig()
      .addSeries(series)
      .setProperty({
        propertyName: 'plotOptions',
        propertyValue: DetailedChartConfigService.COMMON_COLOR_CONFIGURATION,
        defaultValue: {}
      })
      .setProperty({
        propertyName: 'scrollbar.enabled',
        propertyValue: false,
        defaultValue: true
      })
      .setProperty({
        propertyName: 'navigator.enabled',
        propertyValue: false,
        defaultValue: true
      })
      .setProperty({
        propertyName: 'rangeSelector.enabled',
        propertyValue: false,
        defaultValue: true
      })
      .setYAxis(yAxis)
      .setProperty({
        propertyName: 'xAxis.minPadding',
        propertyValue: 0.05,
        defaultValue: 0
      })
      .setProperty({
        propertyName: 'xAxis.labels.autoRotation',
        propertyValue: false,
        defaultValue: {}
      })
      .setProperty({
        propertyName: 'xAxis.labels.padding',
        propertyValue: 1,
        defaultValue: 4
      })
      .setProperty({
        propertyName: 'xAxis.units',
        propertyValue: [['day', [1]], ['week', [1]]],
        defaultValue: null
      })
      .setProperty({
        propertyName: 'tooltip',
        propertyValue: DetailedChartConfigService.TOOLTIP_OPTIONS,
        defaultValue: {}
      })
      .setProperty({
        propertyName: 'responsive.rules',
        propertyValue: [
          {
            condition: {
              callback(): boolean {
                return this.chartWidth < 500 && this.options.xAxis[0].tickPixelInterval >= 100;
              }
            },
            chartOptions: {
              xAxis: {
                labels: {
                  align: 'center',
                  rotation: -30
                }
              }
            }
          }
        ],
        defaultValue: []
      })
      .getConfig();
  }

  /**
   * update chart series
   */
  protected updateChartSeries(seriesIndex: number,
                              seriesData: QuoteTimeSeriesType,
                              chartConfig: Highcharts.Options,
                              formatConfig: FormatConfig,
                              isPennyStock: boolean,
                              showOnlyDate: boolean = true): void {
    formatConfig.marker = seriesData !== undefined && seriesData.length < 2;

    if (formatConfig.useStaticIntradayChart === true) {
      this.configureIntradayChart(chartConfig, formatConfig.marketOpenTime);
    } else {
      formatConfig.marketOpenTime = <number>(seriesData !== undefined && seriesData.length !== undefined && seriesData[0] !== undefined ? seriesData[0][0] : null);
      this.configureInterdayChart(chartConfig, formatConfig);
    }
    this.chartConfigService
      .createUpdateConfigBuilder(chartConfig)
      .setProperty({
        propertyName: 'xAxis.tickPixelInterval',
        propertyValue: formatConfig.xAxisTickInterval,
        defaultValue: 0
      })
      .setProperty({
        propertyName: 'tooltip.formatter',
        propertyValue: this.chartFormatterService.createTooltipFormatter(isPennyStock, showOnlyDate),
        defaultValue: {}
      })
      .setProperty({
        propertyName: 'accessibility.point.descriptionFormatter',
        propertyValue: this.chartFormatterService.createPointDescriptionFormatter(showOnlyDate),
        defaultValue: {}
      })
      .setProperty({
        propertyName: 'xAxis.labels.formatter',
        propertyValue: this.chartFormatterService
          .createXAxisLabelFormatter(formatConfig.xAxisTickLabelFormatKey,
            formatConfig.xAxisTickLabelFormatIncludedYear),
        defaultValue: {}
      })
      .setProperty({
        propertyName: 'plotOptions.series.marker.enabled',
        propertyValue: formatConfig.marker,
        defaultValue: false
      })
      .updateSeries(seriesIndex, seriesData);
  }

  /**
   * Configure intraday chart
   *
   * @param config highchart chart object
   * @param marketOpenTime milliseconds
   */
  private configureIntradayChart(config: Highcharts.Options, marketOpenTime: number): void {
    const oneHour = (3600 * 1000);
    const tradingSession = (6.5 * oneHour);
    let date = new Date();

    try {
      if (marketOpenTime !== undefined && marketOpenTime >= 0) {
        date = new Date(marketOpenTime);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      date = new Date();
    }
    const positioner = (): Array<number> => {
      const positions: Array<number> = [];
      let tick = date.getTime() + (oneHour / 2);
      let i = 0;
      for (tick; i < 7; tick += oneHour) {
        positions.push(tick);
        i++;
      }
      return positions;
    };

    this.chartConfigService
      .createUpdateConfigBuilder(config)
      .setProperty({
        propertyName: 'xAxis.startOnTick',
        propertyValue: false,
        defaultValue: false
      })
      .setProperty({
        propertyName: 'xAxis.ordinal',
        propertyValue: false,
        defaultValue: true
      })
      .setProperty({
        propertyName: 'xAxis.min',
        propertyValue: marketOpenTime,
        defaultValue: null
      })
      .setProperty({
        propertyName: 'xAxis.max',
        propertyValue: (marketOpenTime + tradingSession),
        defaultValue: null
      })
      .setProperty({
        propertyName: 'xAxis.minRange',
        propertyValue: tradingSession,
        defaultValue: 0
      })
      .setProperty({
        propertyName: 'xAxis.tickPositioner',
        propertyValue: positioner,
        defaultValue: undefined
      });
  }

  private configureInterdayChart(chartConfig: Highcharts.Options, formatConfig: FormatConfig): void {
    this.chartConfigService
      .createUpdateConfigBuilder(chartConfig)
      .setProperty({
        propertyName: 'xAxis.min',
        propertyValue: formatConfig.marketOpenTime,
        defaultValue: null
      })
      .setProperty({
        propertyName: 'xAxis.max',
        propertyValue: null,
        defaultValue: null
      })
      .setProperty({
        propertyName: 'xAxis.minRange',
        propertyValue: null,
        defaultValue: 0
      })
      .setProperty({
        propertyName: 'xAxis.startOnTick',
        propertyValue: false,
        defaultValue: false
      })
      .setProperty({
        propertyName: 'xAxis.tickPositioner',
        propertyValue: formatConfig.xAxisTickPositioner,
        defaultValue: undefined
      })
      .setProperty({
        propertyName: 'xAxis.ordinal',
        propertyValue: formatConfig.xAxisOrdinal !== undefined ? formatConfig.xAxisOrdinal : true,
        defaultValue: true
      });
  }

  private createSeries(tickerSymbol: string): unknown {
    // Highcharts.SeriesOptions missing type for dataGrouping
    return {
      name: tickerSymbol + ' ' + this.translateService.instant('tdWb.markets.quote.chartConfig.CANDLE_CLOSE'),
      type: null,
      data: null,
      threshold: null,
      dataGrouping: {
        enabled: false
      },
      yAxis: 0
    };
  }

  /**
   * Creates and return series object for volume chart
   */
  private createVolume(data: Array<unknown>): unknown {
    // Highcharts.SeriesOptions missing type for dataGrouping
    return {
      name: this.translateService.instant('tdWb.markets.quote.chartConfig.VOLUME'),
      type: 'column',
      data,
      yAxis: 1,
      dataGrouping: {
        enabled: false
      }
    };
  }

  /**
   * Creates price plot lines
   *
   * @param plotData
   * @returns
   */
  private createPricePlotline(plotData: PlotData): YAxisPlotLinesOptions {
    const translatedDisplayPriceType = `<div>
        <span style="padding-top: 3px;background-color: rgba(255, 255, 255, 0.75);">
        ${ this.translateService.instant('tdWb.markets.quote.chartConfig.' + plotData.displayName,
    { value: plotData.displayValue }) }</span></div>`;
    const yPlotLines: YAxisPlotLinesOptions = {
      id: 'y-Axis-plotline',
      color: '#545454',
      dashStyle: 'Solid',
      width: 1,
      value: plotData.value,
      label: {
        useHTML: true,
        text: translatedDisplayPriceType,
        textAlign: 'left',
        style: {
          color: '#1c1c1c',
          fontFamily: 'Roboto, Helvetica, sans',
          fontSize: '13px'
        }
      },
      zIndex: 1
    };
    return yPlotLines;
  }
}
